import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';
import { CustomDatePipe } from '../../core/pipes/date.pipe';
import { SessionService } from '../../core/services/session.service';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { AdminMessagesService, ContactMessage } from '../../core/services/admin-messages.service';
import { NgxTgModule, TranslateService } from '@worthy-ventures/metaglotta-ngx';

/**
 * Inbox for the public contact form. Replies happen over email/phone, so the only
 * thing editable here is whether a message has been dealt with.
 */
@Component({
    selector: 'app-messages',
    imports: [CommonModule, FormsModule, MenuBarComponent, FooterComponent, AdminNavComponent, CustomDatePipe, NgxTgModule],
    templateUrl: './messages.component.html',
    styleUrl: './messages.component.scss',
})
export class MessagesComponent implements OnInit {
    messages: ContactMessage[] = [];
    loading = false;
    search = '';
    /** Which messages to show. Defaults to open, since that is the work list. */
    filter: 'open' | 'resolved' | 'all' = 'open';
    /** Ids with a resolve request in flight, so their button can be disabled. */
    saving = new Set<string>();

    constructor(
        private messagesService: AdminMessagesService,
        private session: SessionService,
        private router: Router,
        private toastr: ToastrService,
        private i18n: TranslateService,
    ) { }

    ngOnInit(): void {
        // Admin-only: the canAccessOrders flag grants the overview and orders,
        // not the contact inbox. The route guard enforces this too; this is the
        // in-component backstop.
        if (!this.session.isLoggedIn() || !this.session.isAdmin()) {
            this.router.navigate(['/']);
            return;
        }
        this.load();
    }

    get openCount(): number {
        return this.messages.filter((m) => !m.resolved).length;
    }

    get resolvedCount(): number {
        return this.messages.filter((m) => m.resolved).length;
    }

    get filteredMessages(): ContactMessage[] {
        const q = this.search.trim().toLowerCase();
        return this.messages
            .filter((m) => this.filter === 'all' || (this.filter === 'open' ? !m.resolved : m.resolved))
            .filter((m) => !q ||
                (m.name || '').toLowerCase().includes(q) ||
                (m.email || '').toLowerCase().includes(q) ||
                (m.phoneNumber || '').toLowerCase().includes(q) ||
                (m.message || '').toLowerCase().includes(q),
            );
    }

    public setFilter(filter: 'open' | 'resolved' | 'all'): void {
        this.filter = filter;
    }

    public isSaving(m: ContactMessage): boolean {
        return this.saving.has(m.id);
    }

    /**
     * Flips the resolved flag. The card updates immediately and is put back if
     * the request fails, so the list never shows a state the backend rejected.
     */
    public toggleResolved(m: ContactMessage): void {
        if (this.saving.has(m.id)) return;

        const next = !m.resolved;
        m.resolved = next;
        this.saving.add(m.id);

        const previousResolver = m.resolvedByName;

        this.messagesService.setResolved(m.id, next)
            .pipe(finalize(() => { this.saving.delete(m.id); }))
            .subscribe({
                next: (res) => {
                    // the backend stamps resolvedBy from the session, so take the
                    // name it reports rather than guessing here
                    m.resolvedByName = next ? (res?.resolvedByName ?? null) : null;
                    this.toastr.success(this.i18n.instant(next ? 'messages_toast_resolved' : 'messages_toast_reopened'));
                },
                error: () => {
                    m.resolved = !next;
                    m.resolvedByName = previousResolver;
                    this.toastr.error(this.i18n.instant('messages_toast_error'));
                },
            });
    }

    load(): void {
        this.loading = true;
        this.messagesService.list().subscribe({
            next: (res) => { this.messages = res?.messages ?? []; this.loading = false; },
            error: () => { this.messages = []; this.loading = false; },
        });
    }

    /** Initials for the avatar; falls back to the email when no name was sent. */
    public initials(m: ContactMessage): string {
        const source = (m.name || m.email || '?').trim();
        const parts = source.split(/[\s.@_-]+/).filter(Boolean);
        return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
    }
}
